/* FORUM — The Hang
 * ===========================================================================
 * What a Photo Cache card opens. The stop's photographs are hung salon-style
 * in gilded frames on a lit wall; clicking one walks up to it and sets a
 * museum tombstone beside it.
 *
 * It replaces the three.js gallery room, so it has to carry the same weight
 * with none of the machinery: no WebGL, no framework, no build step, and
 * nothing fetched that the theme does not already serve.
 *
 * The contract the section wires to:
 *
 *   FORUM_HANG.mount({ host, assets, tone, onClose, onPiece })
 *   FORUM_HANG.open(stop, photoIndex)   // null index = the wall
 *   FORUM_HANG.close()
 *   FORUM_HANG.isOpen()
 *
 * A stop may name its place's own frames as stop.frames (V3.0), a key of
 * FRAME_SETS below; a stop that names none hangs in the house frames.
 *
 * mount() builds the DOM inside host once and binds every listener the
 * module will ever own. open() is safe to call over and over with different
 * stops; close() leaves nothing bound behind it. The geometry is written
 * into custom properties rather than into inline width/height, because
 * museum-hang.css.liquid has to be free to mean something different by them
 * on a phone — that is how one set of markup carries both hangs.
 * ===========================================================================
 */

(function () {
  'use strict';

  /* A section reload re-runs every asset the section loads. The module is a
     singleton; the second run must do nothing at all. */
  if (window.FORUM_HANG) return;

  /* ---- the frames --------------------------------------------------------
     r is the frame art's aspect (width / height). l, t, w, h are the opening
     as a percentage of the frame box — measured off the art, which is why
     they are not symmetrical. The photograph is laid into the opening one
     percent proud on every side so no seam shows at the rabbet. */
  var FRAMES = {
    ornate: { r: 1.4433, l: 14.43, t: 19.18, w: 71.29, h: 61.65 },
    bead:   { r: 1.2475, l: 10.65, t: 13.48, w: 78.71, h: 73.44 },
    braid:  { r: 1.2525, l: 7.26,  t: 8.89,  w: 85.65, h: 82.42 },
    plain:  { r: 0.8,    l: 15.28, t: 12.50, w: 69.44, h: 74.72 },
    carve:  { r: 0.8127, l: 10.36, t: 8.25,  w: 79.49, h: 83.51 }
  };

  /* ---- each place's own frames -------------------------------------------
     V3.0 (Josh, 2026-09-29): Josh asked for frames that belong to the place,
     different for each and right for where it is, a small set per place,
     rather than the one house set on every wall. A stop names its set with
     `frames`. A set carries the house's five roles, measured the same way as
     FRAMES, and a role's art is museum-frame-<set>-<role>.webp. A role a set
     has no entry for, and a stop with no set, hang in the house frames.
     `grow` (1 if absent) lets art whose frame is heavy for its opening stand
     larger than its slot; fitSlot says how far it may.
     The numbers between the two markers are written by a script from the
     finished art, so the markers keep their own lines and nothing else goes
     between them. On 2026-09-29 they were the magenta stand-ins' numbers,
     there to test the wiring until the art came. */
  /* FRAME_SETS:BEGIN */
  var FRAME_SETS = {
    'rome': {
      ornate:  { r: 1.2216, l: 25.85, t: 36.31, w: 48.07, h: 36.36, grow: 1.1 },
      bead:    { r: 1.2407, l: 11.60, t: 14.39, w: 76.79, h: 71.22 },
      braid:   { r: 1.1601, l: 20.15, t: 23.39, w: 59.70, h: 53.21 },
      plain:   { r: 0.8020, l: 14.07, t: 11.28, w: 71.85, h: 77.44 },
      carve:   { r: 0.8660, l: 23.58, t: 20.42, w: 52.84, h: 59.15 }
    },
    'tokyo': {
      ornate:  { r: 1.4433, l: 11.66, t: 16.85, w: 76.71, h: 66.34 },
      bead:    { r: 1.2225, l: 13.72, t: 16.88, w: 72.57, h: 66.29 },
      braid:   { r: 1.1669, l: 17.92, t: 21.25, w: 64.19, h: 57.53 },
      plain:   { r: 0.8080, l: 15.28, t: 12.30, w: 69.48, h: 75.44 },
      carve:   { r: 0.8630, l: 21.75, t: 18.50, w: 56.54, h: 63.06 }
    },
    'san-diego': {
      ornate:  { r: 1.5368, l: 6.84, t: 10.20, w: 86.32, h: 79.59 },
      bead:    { r: 1.2107, l: 15.16, t: 18.40, w: 69.74, h: 63.20 },
      braid:   { r: 1.1751, l: 18.25, t: 21.25, w: 63.55, h: 57.50 },
      plain:   { r: 0.8330, l: 21.44, t: 18.00, w: 57.20, h: 64.00 },
      carve:   { r: 0.8450, l: 18.22, t: 15.30, w: 63.55, h: 69.40 }
    },
    'springville': {
      ornate:  { r: 1.3739, l: 16.28, t: 22.26, w: 67.42, h: 55.47 },
      bead:    { r: 1.2920, l: 5.50, t: 6.98, w: 88.98, h: 86.01 },
      braid:   { r: 1.1390, l: 20.30, t: 24.03, w: 59.37, h: 51.94 },
      plain:   { r: 0.8630, l: 24.45, t: 20.31, w: 51.10, h: 59.37 },
      carve:   { r: 0.7990, l: 7.38, t: 6.00, w: 85.24, h: 88.00 }
    }
  };
  /* FRAME_SETS:END */

  var BOX_W = 1300;      // the unit box the desktop hang is drawn in
  var BOX_H = 690;
  var GUTTER = 28;       // units, for the overflow rows only
  var REACH = 13.8;      // units a grown piece may pass its slot: just under half the 28-unit gutter, so two grown neighbours never touch (the tightest drawn gap is 27.73)

  function slot(f, x, y, w) { return { f: f, x: x, y: y, w: w }; }

  /* ---- the hangs ---------------------------------------------------------
     Drawn compositions, not generated ones. Order in these tables is the
     order they were drawn in; the FIG numbers come from sorting by x then y
     further down, so a photograph's number is a function of where it hangs
     and nothing else. */
  var HANG_12 = [
    slot('bead',    0, 158, 214),
    slot('carve',  242, 133, 160),
    slot('plain',    0, 358, 160),
    slot('braid',  188, 358, 214),
    slot('ornate', 430, 192, 440),
    slot('braid',  550,   4, 200),
    slot('bead',   550, 525, 200),
    slot('carve',  898, 133, 160),
    slot('ornate',1086, 182, 214),
    slot('bead',   898, 358, 214),
    slot('plain', 1140, 358, 160),
    slot('braid', 1150,  24, 150)
  ];

  /* Ten is twelve without the two right-hand fillers. */
  var HANG_10 = HANG_12.slice(0, 10);

  /* V3.0 (Josh, 2026-09-29): the eight-piece wall used to keep the left
     column at the box's edge, 216 units from the centre frame, while the
     right column sat 28 from it. Josh: "frames on left brought closer to
     center." The left column now sits 28 from the centre like the right,
     and the whole group is centred in the box (94 units of air each side). */
  var HANG_8 = [
    slot('bead',    94, 158, 214),
    slot('plain',  148, 358, 160),
    slot('ornate', 336, 192, 440),
    slot('braid',  456,   4, 200),
    slot('bead',   456, 525, 200),
    slot('carve',  804, 133, 160),
    slot('ornate', 992, 182, 214),
    slot('bead',   804, 358, 214)
  ];

  var HANG_6 = [
    slot('bead',   188, 158, 214),
    slot('plain',  228, 358, 160),
    slot('ornate', 430, 192, 440),
    slot('braid',  550,   4, 200),
    slot('carve',  898, 133, 160),
    slot('bead',   898, 358, 214)
  ];

  var HANGS = { 6: HANG_6, 8: HANG_8, 10: HANG_10, 12: HANG_12 };

  /* ---- the fallback hang -------------------------------------------------
     Content changes, and a count nobody drew for still has to hang straight.
     The rule: the big ornate anchor is always the centre, and the rest of the
     twelve-slot table is added outward in mirrored pairs — inner cluster
     first, then the outer columns, then the pieces above and below the
     anchor, then the top corner. Every slot comes from a composition that is
     known not to overlap, so no subset of it can.
     Past twelve there is nothing left to borrow, so the hang continues below
     in rows of the bead 214 / plain 160 pair with a 28-unit gutter, and the
     unit box grows to hold them. Nothing overlaps and nothing leaves the box;
     the box is simply taller, and the whole thing scales down to fit. */
  var LADDER = [
    slot('carve',  242, 133, 160),   // left, inner
    slot('carve',  898, 133, 160),   // right, inner
    slot('braid',  188, 358, 214),
    slot('bead',   898, 358, 214),
    slot('bead',     0, 158, 214),   // left, outer
    slot('ornate',1086, 182, 214),   // right, outer
    slot('plain',    0, 358, 160),
    slot('plain', 1140, 358, 160),
    slot('braid',  550,   4, 200),   // over the anchor
    slot('bead',   550, 525, 200),   // under it
    slot('braid', 1150,  24, 150)    // the top corner
  ];

  var OVERFLOW_PAIR = [ { f: 'bead', w: 214 }, { f: 'plain', w: 160 } ];

  /* Slots are measured in the house frames whatever hangs in them (V3.0): a
     place's art is fitted inside the slot (fitSlot), so the compositions stay
     exactly as drawn. */
  function slotBottom(s) { return s.y + s.w / FRAMES[s.f].r; }

  function fallbackHang(n) {
    var anchor = slot('ornate', 430, 192, 440);
    if (n <= 1) return { slots: [anchor], boxHu: BOX_H };
    if (n <= 12) {
      return { slots: [anchor].concat(LADDER.slice(0, n - 1)), boxHu: BOX_H };
    }

    var out = HANG_12.slice();
    var left = n - 12;
    var y = 0;
    var i;
    for (i = 0; i < out.length; i++) y = Math.max(y, slotBottom(out[i]));
    y += GUTTER;

    var pick = 0;
    while (left > 0) {
      var x = 0, rowH = 0, placed = 0;
      while (left > 0) {
        var spec = OVERFLOW_PAIR[pick % 2];
        if (x + spec.w > BOX_W) break;
        out.push(slot(spec.f, x, y, spec.w));
        rowH = Math.max(rowH, spec.w / FRAMES[spec.f].r);
        x += spec.w + GUTTER;
        pick++; left--; placed++;
      }
      if (!placed) break;                 // cannot happen; refuses to spin
      y += rowH + GUTTER;
    }
    /* 22 units of air under the last row for its numerals. */
    return { slots: out, boxHu: Math.max(BOX_H, Math.round(y - GUTTER + 22)) };
  }

  function hangFor(n) {
    var drawn = HANGS[n];
    var made = drawn ? { slots: drawn.slice(), boxHu: BOX_H } : fallbackHang(n);
    /* FIG order is geometric: left to right, then top to bottom. */
    made.slots.sort(function (a, b) { return (a.x - b.x) || (a.y - b.y); });
    return made;
  }

  /* ---- a place's art in a drawn slot (V3.0, Josh, 2026-09-29) -----------
     The slot's box is the house frame's: x, y, w wide, w / its r tall. The
     art hangs inside it whole and centred, at its own aspect: the full width
     if it is relatively wider than the box, the full height if not. `grow`
     then scales it about the centre, but no side may pass the box by more
     than REACH; past that it is scaled back down, aspect kept. The drawn
     slots keep 28 units from their neighbours, so a grown piece cannot reach
     one that did not grow. The one tighter spot is under the small ornate at
     1086,182, 27.73 above the bead and the plain below it: were both sides
     of that gap grown the full 14 they would cross by 0.27. The piece takes
     the fitted box, so its numeral, its lift, its focus ring and its click
     all follow the art. With the house art F is the house frame and this
     returns the slot exactly. */
  function fitSlot(desk, F) {
    var bw = desk.w, bh = desk.w / FRAMES[desk.f].r;
    var w = bw, h = bw / F.r;
    if (h > bh) { h = bh; w = bh * F.r; }
    var g = (typeof F.grow === 'number' && F.grow > 0) ? F.grow : 1;
    w *= g; h *= g;
    var k = Math.min(1, (bw + 2 * REACH) / w, (bh + 2 * REACH) / h);
    w *= k; h *= k;
    return { x: desk.x + (bw - w) / 2, y: desk.y + (bh - h) / 2, w: w };
  }

  /* ---- the phone hang ----------------------------------------------------
     A vertical hang that scrolls, against a 390-wide reference with a 350
     column. Four rows repeat: a landscape across the column, a two-up with
     the right one dropped, another landscape, and the two-up mirrored. */
  var RHYTHM = [
    [ { f: 'ornate', w: 350 } ],
    [ { f: 'carve', w: 150 }, { f: 'bead', w: 182, drop: 40 } ],
    [ { f: 'braid', w: 350 } ],
    [ { f: 'bead', w: 182 }, { f: 'plain', w: 150 } ]
  ];
  var PHONE_REF = 350;

  /* ---- odds and ends ---------------------------------------------------- */
  var PHONE_Q = '(max-width: 749px)';
  var EYE_Q = '(hover: hover) and (pointer: fine)';

  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function pct(a, b) { return (Math.round((a / b) * 1e6) / 1e4) + '%'; }
  function px(n) { return Math.round(n * 100) / 100 + 'px'; }
  function isPhone() { return window.matchMedia(PHONE_Q).matches; }

  var st = {
    mounted: false, bound: false,
    host: null, root: null, box: null, stage: null, eye: null,
    assets: null, base: '', tone: 'dark',
    onClose: null, onPiece: null,
    els: {},
    stop: null, photos: [], frames: '', slots: [], boxHu: BOX_H, sig: '',
    deskFrame: [], phoneFrame: [],
    pieces: [],
    view: 'closed', cur: -1, live: 0,
    opener: null, hintRetired: false,
    hideTimer: 0, preloaded: {}
  };

  /* ---- assets ------------------------------------------------------------
     The section publishes MUSEUM_ASSETS as { 'museum-x.webp': cdnUrl }. mount
     may hand the same map in directly; a name in neither falls through to an
     asset base, and then to itself, which is what keeps a rehearsal page
     working with the files sitting next to it. */
  function asset(name) {
    var m = st.assets || window.MUSEUM_ASSETS || {};
    if (m && m[name]) return m[name];
    var w = window.MUSEUM_ASSETS || {};
    if (w[name]) return w[name];
    return st.base ? st.base + name : name;
  }

  function frameUrl(name) { return asset('museum-frame-' + name + '.webp'); }

  /* The art a role hangs in on the open stop's wall (V3.0): the place's own
     if its set has that role, the house frame's if not. The wall, the piece
     view and the peeks all ask here, so no house frame turns up on a place
     that has its own. */
  function art(role) {
    var set = st.frames ? FRAME_SETS[st.frames] : null;
    if (set && set[role]) {
      return { F: set[role], url: asset('museum-frame-' + st.frames + '-' + role + '.webp') };
    }
    var name = FRAMES[role] ? role : 'ornate';
    return { F: FRAMES[name], url: frameUrl(name) };
  }

  function urlVar(u) { return 'url("' + String(u).replace(/"/g, '%22') + '")'; }

  /* A caption's src may be a URL or a bare asset name. */
  function photoUrl(src) {
    var s = String(src || '');
    if (!s) return '';
    if (/^(?:https?:|data:|blob:|file:|\/\/|\/|\.)/.test(s)) return s;
    return asset(s);
  }

  /* ---- markup ----------------------------------------------------------- */
  var SVG_CHEV_L = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square"><path d="M15 4.5 7.5 12 15 19.5"/></svg>';
  var SVG_GRID = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3.2" y="3.2" width="7.6" height="7.6"/><rect x="13.2" y="3.2" width="7.6" height="7.6"/><rect x="3.2" y="13.2" width="7.6" height="7.6"/><rect x="13.2" y="13.2" width="7.6" height="7.6"/></svg>';
  var SVG_STEP_L = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"><path d="M15 4.5 7.5 12 15 19.5"/></svg>';
  var SVG_STEP_R = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"><path d="M9 4.5 16.5 12 9 19.5"/></svg>';
  var SVG_EYE = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.6 12S5.5 5.2 12 5.2 22.4 12 22.4 12 18.5 18.8 12 18.8 1.6 12 1.6 12z"/><circle cx="12" cy="12" r="3.1"/></svg>';

  var SHELL =
    '<div class="hang">' +
      '<div class="hang-wall">' +
        '<div class="hang-pool"></div>' +
        '<div class="hang-stage"><div class="hang-box"></div></div>' +
      '</div>' +
      '<div class="hang-skirt"></div>' +
      '<div class="hang-floor"><p class="hang-hint">Click a picture to walk up to it</p></div>' +
      '<div class="hang-view">' +
        '<div class="hang-veil"></div>' +
        '<div class="hang-light"></div>' +
        '<button class="hang-peek prev" type="button">' +
          '<img class="hang-peek-photo" alt="" decoding="async"><span class="hang-peek-frame"></span>' +
        '</button>' +
        '<button class="hang-peek next" type="button">' +
          '<img class="hang-peek-photo" alt="" decoding="async"><span class="hang-peek-frame"></span>' +
        '</button>' +
        '<div class="hang-plate" tabindex="-1" role="img">' +
          '<div class="hang-layer"><img class="hang-layer-photo" alt=""><span class="hang-layer-frame"></span></div>' +
          '<div class="hang-layer"><img class="hang-layer-photo" alt=""><span class="hang-layer-frame"></span></div>' +
        '</div>' +
        '<div class="hang-tomb">' +
          '<div class="hang-tomb-text">' +
            '<div class="hang-tomb-block"></div>' +
            '<div class="hang-tomb-block"></div>' +
          '</div>' +
          '<div class="hang-tomb-nav">' +
            '<button class="hang-step prev" type="button" aria-label="Previous photograph"><span>' + SVG_STEP_L + '</span></button>' +
            '<span class="hang-nav-count"></span>' +
            '<button class="hang-step next" type="button" aria-label="Next photograph"><span>' + SVG_STEP_R + '</span></button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<header class="hang-head">' +
        '<button class="hang-hair hang-back" type="button">' + SVG_CHEV_L + '<span>Back to atlas</span></button>' +
        '<div class="hang-ident">' +
          '<div class="hang-eyebrow"></div>' +
          '<h2 class="hang-title"></h2>' +
        '</div>' +
        '<div class="hang-meta"><span class="hang-meta-n"></span><span class="hang-meta-d"></span></div>' +
        '<button class="hang-hair hang-towall" type="button">' + SVG_GRID + '<span>Back to the wall</span></button>' +
      '</header>' +
      '<div class="hang-eye">' + SVG_EYE + '</div>' +
    '</div>';

  var TOMB_BLOCK =
    '<div class="hang-tomb-fig"></div>' +
    '<p class="hang-tomb-caption"></p>' +
    '<div class="hang-tomb-rule"></div>' +
    '<div class="hang-tomb-where"></div>' +
    '<div class="hang-tomb-when"></div>';

  /* ---- mount ------------------------------------------------------------ */
  function mount(opts) {
    opts = opts || {};
    var host = opts.host;
    if (!host || !host.nodeType) return api;

    /* A section reload hands over a new host with the old one detached. Same
       module, new room: rebuild into the new host rather than refusing. */
    if (st.mounted && st.host === host) {
      if (opts.assets) st.assets = opts.assets;
      if (opts.tone) setTone(opts.tone);
      if (opts.onClose) st.onClose = opts.onClose;
      if (opts.onPiece) st.onPiece = opts.onPiece;
      return api;
    }

    /* Leave the old host as we found it, so a detached section takes none of
       our attributes with it. */
    if (st.host && st.host !== host) {
      if (st.hideTimer) { clearTimeout(st.hideTimer); st.hideTimer = 0; }
      st.host.classList.remove('hang-host', 'hang-open', 'hang-at-piece', 'hang-hint-retired');
      st.host.removeAttribute('role');
      st.host.removeAttribute('aria-modal');
      st.host.removeAttribute('aria-label');
      st.host.removeAttribute('data-tone');
      st.host.style.display = '';
      st.host.innerHTML = '';
    }

    st.host = host;
    st.assets = opts.assets || null;
    st.base = opts.assetBase || opts.asset_base || '';
    st.onClose = typeof opts.onClose === 'function' ? opts.onClose : null;
    st.onPiece = typeof opts.onPiece === 'function' ? opts.onPiece : null;

    /* A new shell holds no wall yet, so nothing may be remembered about the
       one that was in the old one — otherwise the next open() with the same
       stop would skip the rebuild and hang nothing at all. */
    st.sig = '';
    st.pieces = [];
    st.view = 'closed';
    st.cur = -1;
    st.opener = null;

    host.classList.add('hang-host');
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-modal', 'true');
    host.innerHTML = SHELL;

    st.root = host.querySelector('.hang');
    st.stage = host.querySelector('.hang-stage');
    st.box = host.querySelector('.hang-box');
    st.eye = host.querySelector('.hang-eye');

    var blocks = host.querySelectorAll('.hang-tomb-block');
    blocks[0].innerHTML = TOMB_BLOCK;
    blocks[1].innerHTML = TOMB_BLOCK;

    st.els = {
      head: host.querySelector('.hang-head'),
      eyebrow: host.querySelector('.hang-eyebrow'),
      title: host.querySelector('.hang-title'),
      meta: host.querySelector('.hang-meta'),
      back: host.querySelector('.hang-back'),
      metaN: host.querySelector('.hang-meta-n'),
      metaD: host.querySelector('.hang-meta-d'),
      toWall: host.querySelector('.hang-towall'),
      view: host.querySelector('.hang-view'),
      light: host.querySelector('.hang-light'),
      plate: host.querySelector('.hang-plate'),
      layers: host.querySelectorAll('.hang-layer'),
      peekPrev: host.querySelector('.hang-peek.prev'),
      peekNext: host.querySelector('.hang-peek.next'),
      tomb: host.querySelector('.hang-tomb'),
      tombBlocks: blocks,
      count: host.querySelector('.hang-nav-count'),
      stepPrev: host.querySelector('.hang-tomb-nav .hang-step.prev'),
      stepNext: host.querySelector('.hang-tomb-nav .hang-step.next')
    };

    setTone(opts.tone || 'dark');

    /* ---- every listener the module owns ----
       The ones on this shell go with this shell: a section reload throws the
       host away and these with it. The wall's pieces are reached by
       delegation on the box, so opening a different stop swaps hundreds of
       nodes without binding or leaking a thing. */
    st.box.addEventListener('click', onWallClick);
    st.els.back.addEventListener('click', requestClose);
    st.els.toWall.addEventListener('click', toWall);
    st.els.stepPrev.addEventListener('click', function () { step(-1); });
    st.els.stepNext.addEventListener('click', function () { step(1); });
    st.els.peekPrev.addEventListener('click', function () { step(-1); });
    st.els.peekNext.addEventListener('click', function () { step(1); });
    st.root.addEventListener('pointermove', onPointerMove);
    st.root.addEventListener('pointerleave', eyeOff);

    /* The three on the document and the window are not the shell's, so they
       are bound once for the life of the page however often a section
       reloads and hands over a new host. */
    if (!st.bound) {
      document.addEventListener('keydown', onKey, true);
      document.addEventListener('focusin', onFocusIn, true);
      window.addEventListener('resize', onResize);
      st.bound = true;
    }

    st.mounted = true;
    measure();
    return api;
  }

  function setTone(tone) {
    st.tone = tone === 'pale' ? 'pale' : 'dark';
    if (st.host) st.host.setAttribute('data-tone', st.tone);
  }

  /* The host is fixed, so 100vh is almost always right — but its owner is
     free to inset it, and the unit box is sized off the number either way. */
  function measure() {
    if (!st.host) return;
    var h = st.host.clientHeight;
    if (h > 0) st.host.style.setProperty('--hang-vh', px(h));
  }

  /* ---- building the wall ------------------------------------------------ */
  function buildWall() {
    var photos = st.photos;
    var made = hangFor(photos.length);
    st.slots = made.slots;
    st.boxHu = made.boxHu;
    st.host.style.setProperty('--hang-box-hu', String(st.boxHu));

    st.box.textContent = '';
    st.pieces = [];
    st.deskFrame = [];
    st.phoneFrame = [];

    var i = 0, row = 0;
    while (i < photos.length) {
      var spec = RHYTHM[row % RHYTHM.length];
      var rowEl = document.createElement('div');
      rowEl.className = 'hang-row';
      for (var k = 0; k < spec.length && i < photos.length; k++, i++) {
        rowEl.appendChild(makePiece(i, spec[k], st.slots[i]));
      }
      st.box.appendChild(rowEl);
      row++;
    }
  }

  function makePiece(i, mob, desk) {
    var photo = st.photos[i];
    var dA = art(desk.f), dF = dA.F;
    var mA = art(mob.f), mF = mA.F;
    var fit = fitSlot(desk, dF);

    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'hang-piece';
    b.setAttribute('data-i', String(i));
    b.setAttribute('aria-label', 'Open ' + pad2(i + 1) + ' — ' + (photo.caption || 'photograph'));

    var s = b.style;
    /* the salon wall: the art's own box inside the drawn slot */
    s.setProperty('--px', pct(fit.x, BOX_W));
    s.setProperty('--py', pct(fit.y, st.boxHu));
    s.setProperty('--pw', pct(fit.w, BOX_W));
    s.setProperty('--pr', String(dF.r));
    s.setProperty('--ol', (dF.l - 1) + '%');
    s.setProperty('--ot', (dF.t - 1) + '%');
    s.setProperty('--ow', (dF.w + 2) + '%');
    s.setProperty('--oh', (dF.h + 2) + '%');
    s.setProperty('--frame', urlVar(dA.url));
    /* the vertical hang: the rhythm's width, the art's aspect and opening */
    s.setProperty('--mw', pct(mob.w, PHONE_REF));
    s.setProperty('--mr', String(mF.r));
    s.setProperty('--mol', (mF.l - 1) + '%');
    s.setProperty('--mot', (mF.t - 1) + '%');
    s.setProperty('--mow', (mF.w + 2) + '%');
    s.setProperty('--moh', (mF.h + 2) + '%');
    s.setProperty('--frame-m', urlVar(mA.url));
    if (mob.drop) s.setProperty('--mdrop', pct(mob.drop, PHONE_REF));

    st.deskFrame[i] = desk.f;
    st.phoneFrame[i] = mob.f;

    var img = document.createElement('img');
    img.className = 'hang-piece-photo';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = photoUrl(photo.src);

    var frame = document.createElement('span');
    frame.className = 'hang-piece-frame';

    var fig = document.createElement('span');
    fig.className = 'hang-fig';
    /* Just the numeral, the way it is drawn on the wall. The tombstone is
       where it becomes "Fig. 05 / 12". */
    fig.textContent = pad2(i + 1);

    b.appendChild(img);
    b.appendChild(frame);
    b.appendChild(fig);
    st.pieces[i] = b;
    return b;
  }

  /* ---- open, close ------------------------------------------------------ */
  function open(stop, photoIndex) {
    if (!st.mounted || !stop) return;
    var photos = (stop.photos || []).filter(function (p) { return p && p.src; });
    if (!photos.length) return;

    if (st.hideTimer) { clearTimeout(st.hideTimer); st.hideTimer = 0; }

    var active = document.activeElement;
    if (st.view === 'closed') {
      st.opener = (active && active !== document.body && !st.host.contains(active)) ? active : st.opener;
    }

    /* V3.0 (Josh, 2026-09-29): the place's own frames, when it names a set
       there is. They are part of what the wall is, so a change of set
       rebuilds it just as a change of photographs does. */
    var frames = (stop.frames && Object.prototype.hasOwnProperty.call(FRAME_SETS, stop.frames)) ?
      String(stop.frames) : '';
    var sig = String(stop.index) + '|' + frames + '|' + photos.length + '|' +
      photos.map(function (p) { return p.src; }).join(',');
    st.stop = stop;
    st.photos = photos;
    st.frames = frames;

    if (sig !== st.sig) {
      st.sig = sig;
      buildWall();
    }

    /* A cache titled with its place (V3.0) says the place once. */
    var named = stop.title || 'the stop';
    if (stop.place) { named = stop.place.indexOf(named) === 0 ? stop.place : named + ', ' + stop.place; }
    st.host.setAttribute('aria-label', 'Photo cache — ' + named);
    paintChrome();

    st.host.style.display = 'block';
    /* a frame for display:block to take, so the fade has something to run on */
    void st.host.offsetWidth;
    st.host.classList.add('hang-open');
    measure();

    var idx = (typeof photoIndex === 'number' && photoIndex >= 0) ?
      Math.min(photoIndex, photos.length - 1) : null;

    if (idx === null) {
      st.view = 'wall';
      st.host.classList.remove('hang-at-piece');
      setWallTabbable(true);
      st.cur = -1;
      focusFirst();
    } else {
      st.view = 'piece';
      setWallTabbable(false);
      st.host.classList.add('hang-at-piece');
      st.cur = -1;
      showPiece(idx, true);
    }
  }

  function close() {
    if (!st.mounted || st.view === 'closed') return;
    st.view = 'closed';
    st.host.classList.remove('hang-open', 'hang-at-piece');
    eyeOff();
    setWallTabbable(true);

    var back = st.opener;
    st.opener = null;
    if (back && document.contains(back) && typeof back.focus === 'function') {
      try { back.focus(); } catch (e) { /* the opener may have gone */ }
    } else {
      /* Nothing to go back to: at least do not leave focus on a picture
         nobody can see any more. */
      var here = document.activeElement;
      if (here && st.host.contains(here) && typeof here.blur === 'function') here.blur();
    }

    /* Let the fade finish before the element leaves the layout. */
    st.hideTimer = setTimeout(function () {
      st.hideTimer = 0;
      if (st.view === 'closed' && st.host) st.host.style.display = '';
    }, 360);
  }

  /* The atlas asked to be told, and the room shuts either way: close() is
     idempotent, so a callback that closes us too costs nothing. */
  function requestClose() {
    close();
    if (st.onClose) { try { st.onClose(); } catch (e) { /* not ours */ } }
  }

  /* ---- the chrome ------------------------------------------------------- */
  function paintChrome() {
    var stop = st.stop || {};
    var n = st.photos.length;
    var place = stop.place || stop.kind || '';

    st.els.eyebrow.textContent = '';
    var ink = document.createElement('b');
    ink.textContent = 'Photo Cache';
    st.els.eyebrow.appendChild(ink);
    if (place) st.els.eyebrow.appendChild(document.createTextNode(' · ' + place));

    st.els.title.textContent = stop.title || '';
    /* Two spans: a phone keeps the count and lets the date go. */
    st.els.metaN.textContent = n + (n === 1 ? ' frame' : ' frames');
    st.els.metaD.textContent = stop.date ? ' · ' + stop.date : '';
  }

  /* ---- the wall -------------------------------------------------------- */
  function onWallClick(e) {
    var hit = e.target.closest ? e.target.closest('.hang-piece') : null;
    if (!hit || !st.box.contains(hit)) return;
    var i = parseInt(hit.getAttribute('data-i'), 10);
    if (isNaN(i)) return;
    toPiece(i);
  }

  function setWallTabbable(on) {
    for (var i = 0; i < st.pieces.length; i++) {
      if (on) st.pieces[i].removeAttribute('tabindex');
      else st.pieces[i].setAttribute('tabindex', '-1');
    }
  }

  function toPiece(i) {
    if (st.view === 'closed') return;
    st.view = 'piece';
    setWallTabbable(false);
    st.host.classList.add('hang-at-piece');
    showPiece(i, true);
  }

  function toWall() {
    if (st.view !== 'piece') return;
    st.view = 'wall';
    st.host.classList.remove('hang-at-piece');
    setWallTabbable(true);
    eyeOff();
    var back = st.pieces[st.cur] || st.pieces[0];
    if (back) back.focus();
  }

  /* ---- the piece view -------------------------------------------------- */
  function step(dir) {
    if (st.view !== 'piece') return;
    var to = st.cur + dir;
    if (to < 0 || to >= st.photos.length) return;
    showPiece(to, false);
  }

  function showPiece(i, jump) {
    if (i === st.cur) return;
    var photo = st.photos[i];
    if (!photo) return;
    st.cur = i;

    /* The instruction has been obeyed — however the piece was reached, the
       hint can retire for the session. */
    if (!st.hintRetired) {
      st.hintRetired = true;
      st.host.classList.add('hang-hint-retired');
    }

    var frameName = isPhone() ? st.phoneFrame[i] : st.deskFrame[i];
    var F = art(frameName).F;

    /* the plate: paint the resting layer, then cross-fade to it */
    st.live = st.live ? 0 : 1;
    var layer = st.els.layers[st.live];
    var other = st.els.layers[st.live ? 0 : 1];
    paintLayer(layer, i, frameName);
    layer.classList.add('on');
    other.classList.remove('on');

    st.els.plate.style.setProperty('--plate-r', String(F.r));
    st.els.plate.setAttribute('aria-label', 'Fig. ' + pad2(i + 1) + ' of ' +
      pad2(st.photos.length) + ' — ' + (photo.caption || 'photograph'));

    paintTomb(i);
    paintPeeks(i);
    layoutPiece(jump);

    st.els.stepPrev.disabled = (i === 0);
    st.els.stepNext.disabled = (i === st.photos.length - 1);

    preload(i - 1); preload(i); preload(i + 1);

    if (jump) st.els.plate.focus();
    if (st.onPiece && st.stop) {
      try { st.onPiece(st.stop.index, i); } catch (e) { /* not ours */ }
    }
  }

  function paintLayer(layer, i, frameName) {
    var A = art(frameName), F = A.F;
    var s = layer.style;
    s.setProperty('--lol', (F.l - 1) + '%');
    s.setProperty('--lot', (F.t - 1) + '%');
    s.setProperty('--low', (F.w + 2) + '%');
    s.setProperty('--loh', (F.h + 2) + '%');
    s.setProperty('--lframe', urlVar(A.url));
    var img = layer.querySelector('img');
    var url = photoUrl(st.photos[i].src);
    if (img.getAttribute('src') !== url) img.src = url;
  }

  function paintTomb(i) {
    var stop = st.stop || {};
    var photo = st.photos[i];
    var blocks = st.els.tombBlocks;
    var to = blocks[st.live];
    var from = blocks[st.live ? 0 : 1];
    var line = 'Fig. ' + pad2(i + 1) + ' / ' + pad2(st.photos.length);

    to.querySelector('.hang-tomb-fig').textContent = line;
    to.querySelector('.hang-tomb-caption').textContent = photo.caption || '';
    to.querySelector('.hang-tomb-where').textContent = stop.place || stop.kind || '';
    to.querySelector('.hang-tomb-when').textContent = stop.date || '';
    to.classList.add('on');
    from.classList.remove('on');
    st.els.count.textContent = line;
  }

  function paintPeeks(i) {
    peek(st.els.peekPrev, i - 1);
    peek(st.els.peekNext, i + 1);
  }

  function peek(el, i) {
    var photo = st.photos[i];
    if (!photo) { el.hidden = true; return; }
    var frameName = isPhone() ? st.phoneFrame[i] : st.deskFrame[i];
    var A = art(frameName), F = A.F;
    el.hidden = false;
    el.style.setProperty('--peek-w', px(300 * F.r));
    el.style.setProperty('--kol', (F.l - 1) + '%');
    el.style.setProperty('--kot', (F.t - 1) + '%');
    el.style.setProperty('--kow', (F.w + 2) + '%');
    el.style.setProperty('--koh', (F.h + 2) + '%');
    el.style.setProperty('--kframe', urlVar(A.url));
    el.setAttribute('aria-label', (i < st.cur ? 'Previous: ' : 'Next: ') +
      pad2(i + 1) + ' — ' + (photo.caption || 'photograph'));
    var img = el.querySelector('img');
    var url = photoUrl(photo.src);
    if (img.getAttribute('src') !== url) img.src = url;
  }

  /* Walking up to a picture: the plate is fitted inside a box of
     min(760, 54vw) x min(560, 62vh), keeping the frame's aspect, and centred
     at 42% of the width so the tombstone has the right-hand third. If the
     viewport is too narrow for the label to clear the frame, the frame gives
     up the width rather than the label sliding off the edge. */
  function layoutPiece(jump) {
    if (st.cur < 0) return;
    var host = st.host;
    var vw = host.clientWidth || window.innerWidth;
    var vh = host.clientHeight || window.innerHeight;
    var phone = isPhone();
    var frameName = phone ? st.phoneFrame[st.cur] : st.deskFrame[st.cur];
    var F = art(frameName).F;
    var plate = st.els.plate;

    if (phone) {
      plate.style.removeProperty('--plate-l');
      plate.style.removeProperty('--plate-t');
      plate.style.removeProperty('--plate-w');
      plate.style.removeProperty('--plate-h');
      st.els.tomb.style.removeProperty('--tomb-l');
      st.els.tomb.style.removeProperty('--tomb-t');
      return;
    }

    var TOMB = 300, GAP = 48, EDGE = 24, HEAD = 84, LIGHT_W = 720;
    var cx = vw * 0.42;
    var maxW = Math.min(760, vw * 0.54);
    var maxH = Math.min(560, vh * 0.62);

    /* The label's band ends where the next piece starts peeking in (90px of
       it, plus 12 of air). Under 1180 the peeks are gone — the CSS retires
       them — and the label runs to the edge margin instead. The frame gives
       up width so the label never crosses either. */
    var peeksOn = vw >= 1180;
    var rightEdge = vw - (peeksOn ? 102 : EDGE);
    var room = 2 * (rightEdge - TOMB - GAP - cx);
    if (room > 180) maxW = Math.min(maxW, room);

    var w = maxW, h = w / F.r;
    if (h > maxH) { h = maxH; w = h * F.r; }
    var left = cx - w / 2;
    var top = HEAD + Math.max(16, (vh - HEAD - h) / 2);

    /* Walking up to a picture is not a morph — the morph belongs to stepping
       from one picture to the next. On a jump (opening, or a resize) the
       geometry is written with the transition suppressed and flushed before
       the transition is armed again, so nothing slides in from nowhere. */
    var light = st.els.light;
    if (jump) { plate.classList.add('hang-nojump'); light.classList.add('hang-nojump'); }

    plate.style.setProperty('--plate-l', px(left));
    plate.style.setProperty('--plate-t', px(top));
    plate.style.setProperty('--plate-w', px(w));
    plate.style.setProperty('--plate-h', px(h));

    /* The picture light: 720x400, centred on the frame, its pool of light
       (the gradient's hot spot, 20% down the box) 120px above the top edge. */
    light.style.setProperty('--light-l', px(left + w / 2 - LIGHT_W / 2));
    light.style.setProperty('--light-t', px(top - 120 - 80));

    if (jump) {
      void plate.offsetWidth;          /* flush, so there is nothing to animate */
      plate.classList.remove('hang-nojump');
      light.classList.remove('hang-nojump');
    }

    var tombL = Math.max(EDGE, Math.min(left + w + GAP, rightEdge - TOMB));
    var tombH = st.els.tomb.offsetHeight || 260;
    var tombT = top + h / 2 - tombH / 2;
    tombT = Math.max(HEAD + 16, Math.min(tombT, vh - EDGE - tombH));
    st.els.tomb.style.setProperty('--tomb-l', px(tombL));
    st.els.tomb.style.setProperty('--tomb-t', px(tombT));

    var peekT = px(top + h / 2 - 150);
    st.els.peekPrev.style.setProperty('--peek-t', peekT);
    st.els.peekNext.style.setProperty('--peek-t', peekT);
  }

  /* Only what is on the wall in front of you and the two either side. */
  function preload(i) {
    var photo = st.photos[i];
    if (!photo) return;
    var url = photoUrl(photo.src);
    if (!url || st.preloaded[url]) return;
    st.preloaded[url] = true;
    var img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  /* ---- Cody's eye ------------------------------------------------------- */
  function eyeAllowed() {
    return window.matchMedia(EYE_Q).matches;
  }

  function onPointerMove(e) {
    if (st.view === 'closed' || !eyeAllowed()) return;
    var over = e.target.closest ? e.target.closest('.hang-piece, .hang-peek') : null;
    if (over && over.hidden) over = null;
    if (!over) { eyeOff(); return; }
    st.eye.style.transform = 'translate3d(' + Math.round(e.clientX - 22) + 'px,' +
      Math.round(e.clientY - 22) + 'px, 0)';
    st.eye.classList.add('on');
  }

  function eyeOff() {
    if (st.eye) st.eye.classList.remove('on');
  }

  /* ---- keyboard --------------------------------------------------------- */
  function focusables() {
    var all = st.host.querySelectorAll(
      'button:not([disabled]):not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    var out = [], i;
    for (i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null && !all[i].hidden) out.push(all[i]);
    }
    return out;
  }

  function focusFirst() {
    var target = st.pieces[st.cur >= 0 ? st.cur : 0] || focusables()[0];
    if (target) target.focus();
  }

  function wallPieces() {
    var out = [], i;
    for (i = 0; i < st.pieces.length; i++) out.push(st.pieces[i]);
    return out;
  }

  function onKey(e) {
    if (st.view === 'closed' || !st.host) return;
    var k = e.key;

    if (k === 'Escape' || k === 'Esc') {
      e.preventDefault();
      e.stopPropagation();
      if (st.view === 'piece') toWall();
      else requestClose();
      return;
    }

    if (k === 'ArrowLeft' || k === 'ArrowRight') {
      var dir = (k === 'ArrowRight') ? 1 : -1;
      if (st.view === 'piece') {
        e.preventDefault();
        step(dir);
      } else {
        var pieces = wallPieces();
        if (!pieces.length) return;
        var at = pieces.indexOf(document.activeElement);
        var to = at < 0 ? (dir > 0 ? 0 : pieces.length - 1) :
          Math.min(pieces.length - 1, Math.max(0, at + dir));
        e.preventDefault();
        pieces[to].focus();
      }
      return;
    }

    if (k === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      var here = document.activeElement;
      if (e.shiftKey && (here === first || !st.host.contains(here))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (here === last || !st.host.contains(here))) {
        e.preventDefault(); first.focus();
      }
    }
  }

  /* Belt to the trap's braces: anything that lands outside while the room is
     open gets walked back in. */
  function onFocusIn(e) {
    if (st.view === 'closed' || !st.host) return;
    if (st.host.contains(e.target)) return;
    var f = focusables();
    if (f.length) f[0].focus();
  }

  var resizeTick = 0;
  function onResize() {
    if (st.view === 'closed') return;
    if (resizeTick) return;
    resizeTick = window.requestAnimationFrame ?
      requestAnimationFrame(function () { resizeTick = 0; measure(); layoutPiece(true); }) :
      setTimeout(function () { resizeTick = 0; measure(); layoutPiece(true); }, 60);
  }

  /* ---- the contract ----------------------------------------------------- */
  var api = {
    mount: mount,
    open: open,
    close: close,
    isOpen: function () { return st.view !== 'closed'; },
    version: '1'
  };

  window.FORUM_HANG = api;
})();
